/**
 * 否定词库表格组件
 * 已重构: 列配置和添加弹窗已抽取为独立模块
 * 
 * 原文件: 439行 -> 重构后: 3个文件，每个<200行
 */
import React, { useState } from 'react';
import { Table, Button, Space, Input, Select, Popconfirm, Form, Segmented } from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { NegativeKeyword, NEGATIVE_REASON_OPTIONS } from './types';
import { getNegativeKeywordColumns } from './NegativeKeywordColumns';
import { AddNegativeKeywordModal } from './AddNegativeKeywordModal';


interface NegativeKeywordTableProps {
    keywords: NegativeKeyword[];
    loading: boolean;
    onAdd: (data: Partial<NegativeKeyword>) => Promise<any>;
    onBulkImport?: (keywords: Array<{
        content: string;
        negative_type?: string;
        reason?: string;
        level?: string;
    }>) => Promise<any>;
    onUpdate: (id: number, data: Partial<NegativeKeyword>) => Promise<any>;
    onDelete: (id: number) => Promise<any>;
    onBatchDelete: (ids: number[]) => Promise<any>;
    onBatchUpdateStatus: (ids: number[], isActive: boolean) => Promise<any>;
}

export const NegativeKeywordTable: React.FC<NegativeKeywordTableProps> = ({
    keywords,
    loading,
    onAdd,
    onBulkImport,
    onUpdate,
    onDelete,
    onBatchDelete,
}) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [searchText, setSearchText] = useState('');
    const [filterType, setFilterType] = useState<string | undefined>();
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [addForm] = Form.useForm();
    const [viewMode, setViewMode] = useState<'all' | 'keyword' | 'asin'>('all');

    // 过滤数据
    const safeKeywords = Array.isArray(keywords) ? keywords : [];
    const filteredData = safeKeywords.filter(k => {
        const matchSearch = !searchText || k.content.toLowerCase().includes(searchText.toLowerCase());
        const matchType = !filterType || k.negative_type === filterType;
        const matchView = viewMode === 'all'
            || (viewMode === 'asin' && k.is_asin_negative)
            || (viewMode === 'keyword' && k.is_keyword_negative);
        return matchSearch && matchType && matchView;
    });

    // 获取列配置
    const columns = getNegativeKeywordColumns({ onUpdate, onDelete });

    const handleAdd = async () => {
        try {
            const values = await addForm.validateFields();
            const contentText = values.content?.trim() || '';
            const contentList = contentText.split(/[\n\r]+/).map((c: string) => c.trim()).filter((c: string) => c.length > 0);

            if (contentList.length === 0) return;

            if (contentList.length === 1) {
                await onAdd({
                    content: contentList[0],
                    negative_type: values.negative_type,
                    reason: values.reason,
                    level: values.level,
                    campaign_name: values.campaign_name,
                    notes: values.notes,
                });
            } else if (onBulkImport) {
                await onBulkImport(contentList.map((c: string) => ({
                    content: c,
                    negative_type: values.negative_type,
                    reason: values.reason,
                    level: values.level,
                })));
            } else {
                for (const c of contentList) {
                    await onAdd({ content: c, negative_type: values.negative_type, reason: values.reason, level: values.level });
                }
            }

            setAddModalVisible(false);
            addForm.resetFields();
        } catch (error) {
            console.error('添加失败:', error);
        }
    };

    const handleBatchDelete = async () => {
        if (selectedRowKeys.length === 0) return;
        await onBatchDelete(selectedRowKeys as number[]);
        setSelectedRowKeys([]);
    };

    return (
        <div className="negative-keyword-table">
            {/* 工具栏 */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <Space wrap>
                    <Segmented
                        value={viewMode}
                        onChange={(v) => setViewMode(v as 'all' | 'keyword' | 'asin')}
                        options={[
                            { label: '全部', value: 'all' },
                            { label: '关键词否定', value: 'keyword' },
                            { label: 'ASIN否定', value: 'asin' },
                        ]}
                    />
                    <Input
                        placeholder="搜索"
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 160 }}
                        allowClear
                    />
                    <Select
                        placeholder="原因筛选"
                        value={filterType}
                        onChange={setFilterType}
                        style={{ width: 120 }}
                        allowClear
                        options={Object.entries(NEGATIVE_REASON_OPTIONS).map(([value, label]) => ({ value, label }))}
                    />
                </Space>

                <Space wrap>
                    {selectedRowKeys.length > 0 && (
                        <Popconfirm title={`确定删除 ${selectedRowKeys.length} 个否定词?`} onConfirm={handleBatchDelete}>
                            <Button danger icon={<DeleteOutlined />}>
                                批量删除 ({selectedRowKeys.length})
                            </Button>
                        </Popconfirm>
                    )}
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
                        添加否定词
                    </Button>
                </Space>
            </div>

            {/* 表格 */}
            <Table
                rowKey="id"
                columns={columns}
                dataSource={filteredData || []}
                loading={loading}
                size="small"
                scroll={{ x: 1000 }}
                rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                pagination={{
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条`,
                    defaultPageSize: 20,
                }}
            />

            {/* 添加弹窗 */}
            <AddNegativeKeywordModal
                visible={addModalVisible}
                form={addForm}
                onOk={handleAdd}
                onCancel={() => {
                    setAddModalVisible(false);
                    addForm.resetFields();
                }}
            />
        </div>
    );
};

export default NegativeKeywordTable;
